import { Controller } from "@hotwired/stimulus"

// Stimulus controller for the Kanban board drag-and-drop interaction.
//
// Initializes SortableJS on each column to allow dragging cards between
// columns. When a card is dropped in a new column, sends a PATCH request
// to update the record's status field.
//
// Usage (applied automatically by the _kanban_board.html.erb partial):
//   <div data-controller="kanban"
//        data-kanban-status-field-value="status"
//        data-kanban-update-url-value="/orders/:id/update_status">
//     <div class="kanban-column" data-kanban-target="column" data-status="pending">
//       <div class="kanban-card" data-record-id="1">...</div>
//     </div>
//   </div>
//
export default class extends Controller {
  static targets = ["column", "count"]

  static values = {
    statusField: String,
    updateUrl: String
  }

  connect() {
    if (typeof Sortable === 'undefined') {
      console.warn("SortableJS not loaded — kanban drag-and-drop disabled")
      return
    }

    this.sortables = []
    this.columnTargets.forEach(column => {
      const cardContainer = column.querySelector('.kanban-cards')
      if (!cardContainer) return

      const sortable = new Sortable(cardContainer, {
        group: 'kanban-board',
        animation: 150,
        ghostClass: 'kanban-ghost',
        chosenClass: 'kanban-chosen',
        dragClass: 'kanban-drag',
        handle: '.kanban-card',
        draggable: '.kanban-card',
        onEnd: this._onEnd.bind(this)
      })
      this.sortables.push(sortable)
    })
  }

  disconnect() {
    this.sortables.forEach(s => s.destroy())
    this.sortables = []
  }

  _onEnd(event) {
    const card = event.item
    const newColumn = event.to.closest('.kanban-column')
    const oldColumn = event.from.closest('.kanban-column')
    if (!newColumn || !card) return

    const newStatus = newColumn.dataset.status
    const recordId = card.dataset.recordId

    // Update column counts
    this._updateCounts()

    // If the card moved to a different column, PATCH the status
    if (oldColumn !== newColumn && recordId && newStatus) {
      this._updateStatus(recordId, newStatus, card, oldColumn)
    }
  }

  _updateStatus(recordId, newStatus, card, oldColumn) {
    const url = this.updateUrlValue.replace(':id', recordId)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')
    const body = {}
    body[this.statusFieldValue] = newStatus

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken ? csrfToken.content : '',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
    .then(response => {
      if (!response.ok) {
        // Revert: move card back to old column
        console.error("Status update failed:", response.statusText)
        const oldCards = oldColumn.querySelector('.kanban-cards')
        if (oldCards) {
          oldCards.appendChild(card)
          this._updateCounts()
        }
      } else {
        // Update the status badge on the card if present
        const badge = card.querySelector('.kanban-card-status')
        if (badge) {
          badge.textContent = newStatus
        }
      }
    })
    .catch(error => {
      console.error("Status update error:", error)
      const oldCards = oldColumn.querySelector('.kanban-cards')
      if (oldCards) {
        oldCards.appendChild(card)
        this._updateCounts()
      }
    })
  }

  _updateCounts() {
    this.columnTargets.forEach(column => {
      const cards = column.querySelectorAll('.kanban-card')
      const countBadge = column.querySelector('.kanban-count')
      if (countBadge) {
        countBadge.textContent = cards.length
      }
    })
  }
}
